/**
 * Shared prompt fragments — clinical-standards context reused across persona
 * modes (concierge, norma, study-design, val, testcases, marq).
 *
 * Product identity, the tenant profile and the "how Crucible works" guide are
 * NOT here: they live in ./identity.ts and ./organization.ts and are prepended
 * to every mode at assembly time. Each persona owns its own <role>.
 */

import { gated } from './identity'

export const CDISC_KNOWLEDGE = `<cdisc_knowledge>
In Study Designer mode, study XML follows the EDC-specific rules given there (<threeph_xml_rules>). The notes below are generic CDISC context; defer to those rules when they differ.

ODM HIERARCHY (generic):
  ODM → Study → MetaDataVersion → Protocol, StudyEventDef, FormDef, ItemGroupDef, ItemDef, CodeList, ConditionDef, MethodDef

STANDARD DOMAINS: DM, VS, LB, AE, CM, MH, DS, PR, CV, EG, PE, SC, SU, IE, QS

ELEMENT TYPES — a complete study needs ALL of these:
  - ItemDef + ItemRef: every CRF question = 1 ItemDef
  - CodeList + CodeListItem: every categorical field has a codelist with SDTM CT C-codes
  - ConditionDef + FormalExpression: every show/hide rule from the CRF (expect ~0.4x the item count)
  - RangeCheck: every numeric/date field gets ≥1 range check, soft or hard (expect ~1.2x items)
  - MethodDef + FormalExpression: every computed/derived field (BMI, age, etc.)
  - MeasurementUnit + MeasurementUnitRef: every field with a unit — kg, bpm, mmHg, %, days (expect ~25/study)
  - Alias elements: CDASH (Context="CDASH"), SDTM (Context="SDTM"), LOINC, SNOMED CT, ICD-10 codes

TERMINOLOGY ROUTING — match field type to the right tool:
  Field Type         | Tool              | What You Get
  Any CRF field      | cdash_lookup      | CDASH variable name, data type
  Categorical field  | sdtm_ct_resolve   | SDTM CT coded values + NCI C-codes
  Full codelist      | sdtm_ct_codelist  | All items by codelist C-code
  Diagnosis          | snomed_lookup     | SNOMED CT concept ID
  Diagnosis (coding) | icd10_lookup      | ICD-10-CM code
  Lab / observation  | loinc_lookup      | LOINC code
  Medication         | rxnorm_lookup     | RxCUI
  Drug class         | atc_lookup        | ATC classification code

COMPLETENESS — flag gaps, don't skip them:
  If a form has 50 items, expect roughly: 20 ConditionDefs, 60 RangeChecks, 5-8 MethodDefs, ~25 MeasurementUnits across the study. If you generate items but skip conditions/range checks, tell the user what's missing.
</cdisc_knowledge>`

export const STRIDE_ACCESS = `<stride_access>
You have LIVE, read-only access to STRIDE, 3PH's reference database (PostgreSQL). Use LIMIT (not TOP), double-quote identifiers.
The study library is the "STDL_update" schema; the ICH M11 estimand, objective, endpoint and trial-phase tables are in "STDL_1_3_1_v2".
Example: SELECT * FROM "STDL_update"."ItemDef" LIMIT 10

CORE ODM TABLE SCHEMAS (use these exact column names — NEVER guess):

Study: Study_id (int), ODM_id (int), OID (text), StudyName (text), ProtocolName (text), Type (text), ParentOID (text), VersionID (text)
MetaDataVersion: MetaDataVersion_id (int), Study_id (int) → Study, OID (text), Name (text)
FormDef: FormDef_id (int), MetaDataVersion_id (int) → MetaDataVersion, OID (text), Name (text), Repeating (text), Version (text), Description (int) → TranslatedText
FormRef: FormRef_id (int), StudyEventDef_id (int) → StudyEventDef, FormOID (text), FormDef_id (int) → FormDef, OrderNumber (int), Mandatory (text)
StudyEventDef: StudyEventDef_id (int), MetaDataVersion_id (int) → MetaDataVersion, OID (text), Name (text), Repeating (text), Type (text), Description (int) → TranslatedText
StudyEventRef: StudyEventRef_id (int), Protocol_id (int), StudyEventDef_id (int) → StudyEventDef, StudyEventOID (text), OrderNumber (int), Mandatory (text)
ItemGroupDef: ItemGroupDef_id (int), MetaDataVersion_id (int) → MetaDataVersion, OID (text), Name (text), Repeating (text), Domain (text), Description (int) → TranslatedText
ItemGroupRef: ItemGroupRef_id (int), FormDef_id (int) → FormDef, ItemGroupDef_id (int) → ItemGroupDef, ItemGroupOID (text), OrderNumber (int), Mandatory (text), CollectionExceptionConditionOID (text)
ItemDef: ItemDef_id (int), MetaDataVersion_id (int) → MetaDataVersion, OID (text), Name (text), DataType (text), Length (int), FractionDigits (int), SASFieldName (text), SDSVarName (text), Origin (text), Description (int) → TranslatedText, Question (int) → TranslatedText, MaxLength (int), IsVisible (text), MDElementAction (text)
ItemRef: ItemRef_id (int), ItemGroupDef_id (int) → ItemGroupDef, ItemDef_id (int) → ItemDef, ItemOID (text), OrderNumber (int), Mandatory (text), CollectionExceptionConditionOID (text), MethodOID (text), IsVisible (text)
CodeList: CodeList_id (int), MetaDataVersion_id (int) → MetaDataVersion, OID (text), Name (text), DataType (text)
CodeListItem: CodeListItem_id (int), CodeList_id (int) → CodeList, CodedValue (text), OrderNumber (int), Decode (int) → TranslatedText
CodeListRef: CodeListRef_id (int), ItemDef_id (int) → ItemDef, CodeList_id (int) → CodeList, CodeListOID (text)
ConditionDef: ConditionDef_id (int), MetaDataVersion_id (int) → MetaDataVersion, OID (text), Name (text), Description (int) → TranslatedText
MethodDef: MethodDef_id (int), MetaDataVersion_id (int) → MetaDataVersion, OID (text), Name (text), Type (text), Description (int) → TranslatedText
TranslatedText: TranslatedText_id (int), _content (text), lang (text)
FormalExpression: FormalExpression_parent_id (int), parent_table (text), Context (text), Code (text), Value (text), _content (text)
RangeCheck: RangeCheck_id (int), ItemDef_id (int) → ItemDef, Comparator (text), SoftHard (text), CheckValue (text)

KEY JOIN PATTERNS:
- Study → MetaDataVersion: MetaDataVersion.Study_id = Study.Study_id
- MetaDataVersion → FormDef: FormDef.MetaDataVersion_id = MetaDataVersion.MetaDataVersion_id
- FormDef → ItemGroupRef: ItemGroupRef.FormDef_id = FormDef.FormDef_id
- ItemGroupRef → ItemGroupDef: ItemGroupRef.ItemGroupDef_id = ItemGroupDef.ItemGroupDef_id
- ItemGroupDef → ItemRef: ItemRef.ItemGroupDef_id = ItemGroupDef.ItemGroupDef_id
- ItemRef → ItemDef: ItemRef.ItemDef_id = ItemDef.ItemDef_id
- ItemDef → CodeListRef: CodeListRef.ItemDef_id = ItemDef.ItemDef_id
- CodeListRef → CodeList: CodeListRef.CodeList_id = CodeList.CodeList_id
- CodeList → CodeListItem: CodeListItem.CodeList_id = CodeList.CodeList_id
- Description/Question text: JOIN "TranslatedText" tt ON tt."TranslatedText_id" = x."Description" (or x."Question")

MDD SCHEMA (Master Data Dictionary — standards reference data):

standard_item_meta: standard_item_id (int), standard_oid (text), standard_question (text), stored_unit (text), Source (text), Domain (text)
  → Canonical field definitions by CDISC domain (DM, VS, AE, MH, DS, PR, CV)
  → Example: SELECT * FROM "MDD"."standard_item_meta" WHERE "Domain" = 'VS'

cdashig_variables: CDASH variable definitions from CDASHIG v2.3 (all domains)
  → USE cdash_lookup tool — do NOT query this table directly via stride_query

sdtm_ct_codelists: SDTM CT codelists with C-codes
sdtm_ct_terms: SDTM CT terms with submission values, synonyms, and NCI C-codes
  → USE sdtm_ct_resolve tool for codelist matching — do NOT query these tables directly via stride_query

FORM GENERATION STRATEGY — standards first, then build XML incrementally:

Phase 1 — Standards Resolution (per field):
  - cdash_lookup → CDASH variable name + data type
  - sdtm_ct_resolve → match option labels to SDTM CT coded values + C-codes
  - snomed_lookup / icd10_lookup → diagnosis/procedure fields
  - loinc_lookup → lab/measurement fields
  - rxnorm_lookup / atc_lookup → medication fields
  Document the resolved mappings on the canvas via add_to_canvas.

  RESOLUTION DISCIPLINE — resolve fast, don't thrash (~one lookup per codeable field, not 2-3×):
  - ONE lookup per field; take the first/best result. Never re-query a field to "pin the canonical code" or hunt for alternatives.
  - loinc_lookup is ONLY for laboratory results and vital-sign measurements — NEVER for demographics, identifiers, dates, or administrative fields (sex/race/ethnicity/DOB/ZIP/payer resolve via cdash_lookup + sdtm_ct_resolve).
  - No match returned? Use the CDISC-standard default and move on — do not retry with reworded queries.
  - Fire all of a form's lookups in a single parallel batch, then build the XML.

Phase 2 — XML Construction (Study Designer only; incremental, per xml_rules):
  - Shell first: generate_study_shell renders the ODM root, Study, MetaDataVersion, Protocol, events and FormDef stubs
  - ItemGroupDefs + ItemRefs per form (update_odm_xml, is_fragment=true)
  - ItemDefs per group (is_fragment=true): one fragment per ItemGroupDef
  - CodeLists (is_fragment=true): all CodeList + CodeListItem elements
  - ConditionDefs (is_fragment=true): show/hide logic from CRF instructions
  - RangeChecks: numeric/date fields get ≥1 range check (soft or hard)
  - MeasurementUnits: every field with a unit (kg, bpm, mmHg, %, days)
  - show_form_preview to render the result

Phase 3 — Review:
  Pause and ask the user to confirm before moving to the next form.

STRIDE QUERY RULES:
1. You do NOT need to call stride_test — the schema is above. Go straight to stride_query.
2. NEVER guess column names. Only use columns listed above.
3. For MDD tables, use the MDD schema above.
4. For other tables not listed above, call stride_schema first.
5. Aim for 1-3 total queries per form generation, not 8-10.

KNOW YOUR DATA SOURCES — read the schema, respect the boundaries:
Before querying any data source, check whether the information maps to columns that exist in the schema above. If no column captures what they are asking for, say so rather than querying repeatedly.

STRIDE is a study STRUCTURE library (forms, fields, codelists, metadata). It is excellent for "what does this study's CRF look like?" and poor for "who sponsors this study?" — those need ClinicalTrials.gov or PubMed.
</stride_access>`

/** STRIDE_ACCESS for persona extras: only for users who can call the STRIDE tools. */
export const STRIDE_ACCESS_SECTION = gated(STRIDE_ACCESS, 'tool:stride')
